/// FOURJS_START_COPYRIGHT(D,2014)
/// Property of Four Js*
/// (c) Copyright Four Js 2014, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import gbcLocales from "virtual:gbcLocales";

modulum("I18NService", ["InitService"], function (context, cls) {
  /**
   * Localization Service to translate the app
   * @namespace gbc.I18NService
   * @gbcService
   * @publicdoc
   */
  class I18NService /** @lends gbc.I18NService */ {
    constructor() {
      this._init = false;

      /**
       * Language to use if not defined by user or browser
       * @type {string}
       */
      this._fallBackLng = "en-US";

      /**
       * Event listener object
       * @type {classes.EventListener}
       */
      this._eventListener = new cls.EventListener(); //used to listen when i18next is ready

      /**
       * Event name once I18N is ready
       */
      this._i18nReady = "i18nReady";
      this.change = "change";

      /**
       * Flag to determine if service is ready or not
       * @type {boolean}
       */
      this.isReady = false;

      this.gbcLocales = null;
      this.i18next = null;
    }

    /**
     * Init service method. should be called only once.
     */
    init() {
      this.i18next = window.i18next || i18next;
      this.gbcLocales = window.gbcLocales || gbcLocales;

      this.i18next.use(window.i18nextBrowserLanguageDetector || LanguageDetector).init(
        {
          fallbackLng: this._getNormalizedLanguage(),
          resources: this.gbcLocales,
          detection: {
            lookupQuerystring: "setLng",
            lookupCookie: "lang",
            caches: ["cookie"]
          }
        },
        this._onReady.bind(this)
      );

      // If locale changed in another tab for this app, switch it here!
      gbc.StoredSettingsService.onChange("gbc_locale", (newLocale) => {
        this.setLng(newLocale);
      });
    }

    /**
     * Set the language of the app
     * @param {string} locale language code to set (e.g. en_US, fr_FR ...)
     * @publicdoc
     */
    setLng(locale) {
      this.i18next.changeLanguage(this._getNormalizedLanguage(locale)[0]);
      this._eventListener.emit(this.change);
    }

    whenLangChange(hook) {
      return this._eventListener.when(this.change, hook);
    }

    /**
     * Translate a widget with i18n data
     * @param {classes.WidgetBase} widget Widget to translate
     * @publicdoc
     */
    translate(widget) {
      if (this.isReady) {
        widget.translate();
      } else {
        return this._eventListener.when(this._i18nReady, widget.translate.bind(widget));
      }
      return null;
    }

    /**
     * Translate the given key in the current language
     * @param key
     * @returns {string} The string associated to the key in the current language
     */
    t(key) {
      return this.i18next.t(key);
    }

    /**
     * Get all available translations with locales id
     * @returns {Array} - array of locales id
     * @publicdoc
     */
    getAllLng() {
      return Object.keys(this.gbcLocales).map((key) => {
        if (key !== "undefined") {
          return {
            locale: key,
            language: this.i18next.exists("gwc.lngName", {
              lng: key,
              fallbackLng: "undef"
            })
              ? this.i18next.t("gwc.lngName", {
                  lng: key
                })
              : key
          };
        }
      });
    }

    /**
     * Ready Handler
     * @private
     */
    _onReady() {
      this._checkLanguageCompatibility();
      const storedLng = gbc.StoredSettingsService.loadLanguage();
      const cookieLng = gbc.StoredSettingsService._getCookie("lang");
      const language = cookieLng || storedLng || this._fallBackLng;
      document.querySelector("html").setAttribute("lang", language.substring(0, 2));
      // Emit I18n ready
      this._eventListener.emit(this._i18nReady);
      this.isReady = true;
    }

    /**
     * Try to find the closest language as defined by browser
     * @private
     */
    _checkLanguageCompatibility() {
      const storedLng = gbc.StoredSettingsService.loadLanguage();
      const cookieLng = gbc.StoredSettingsService._getCookie("lang");
      const language = cookieLng || storedLng || this._fallBackLng;

      if (language && !this.gbcLocales[language]) {
        const allLngKeys = Object.keys(this.gbcLocales);
        for (const element of allLngKeys) {
          if (language.startsWith(element.substring(0, 2))) {
            this.setLng(element);
            gbc.StoredSettingsService.saveLocale(element);
            break;
          }
        }
      }
    }

    /**
     * Helper to get the browser current language
     * @returns {string} - locale id
     * @public
     */
    getBrowserLanguage() {
      return navigator.language || navigator.userLanguage;
    }

    /**
     * Get the normalized locale according to the parameter
     * Will do its best to identify an existing locale
     * @param {string} [locale] - initial str to test, if undef, will take browser language, or fallback one: en-US
     * @return {Array} list of usable locales as fallback (i.e: ["fr-FR","en-US"]) this list allways contain at least one item
     * @private
     */
    _getNormalizedLanguage(locale) {
      locale = typeof locale === "undefined" ? false : locale;
      const fallback = locale || this.getBrowserLanguage() || this._fallBackLng;
      const availableLC = Object.keys(this.gbcLocales);
      const fallbackLanguageList = [];
      const localeRegex = /^([a-z]{2})[-_]?([A-Z0-9]*)/;
      let r = null;
      const fallbackR = localeRegex.exec(fallback); // try to match fallback on standardized locale format

      if (fallbackR) {
        for (const element of availableLC) {
          r = localeRegex.exec(element);
          // Check perfect match (i.e 'es-ES'), then taking care of lang without region (i.e 'es-419', 'es_AR' will take only 'es' into account)
          if (element.indexOf(fallback) === 0 || r[1] === fallbackR[1]) {
            if (element === (locale && locale.replace("_", "-"))) {
              // add it at beginning of the array if the locale match
              fallbackLanguageList.unshift(element);
            } else {
              // add it at the end of the array
              fallbackLanguageList.push(element);
            }
          }
        }
      }
      // Use en-US as default if nothing found
      if (fallbackLanguageList.indexOf(this._fallBackLng) === -1) {
        fallbackLanguageList.push(this._fallBackLng);
      }
      return fallbackLanguageList;
    }
  }

  gbc.I18NService = new I18NService();

  context.InitService.register(context.I18NService);
});
